"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const BREAKPOINTS = {
    mobile: { cols: "grid-cols-1", maxW: "300px", icon: "smartphone-bold", labelKey: "Bs_grid_bp_mobile", col: "col-12" },
    tablet: { cols: "grid-cols-2", maxW: "560px", icon: "tablet-bold", labelKey: "Bs_grid_bp_tablet", col: "col-md-6" },
    desktop: { cols: "grid-cols-3", maxW: "760px", icon: "monitor-bold", labelKey: "Bs_grid_bp_desktop", col: "col-lg-4" },
};
const ORDER = ["mobile", "tablet", "desktop"];
const CARD_ICONS = [
    "chart-2-bold-duotone",
    "cart-large-bold-duotone",
    "user-rounded-bold-duotone",
    "gallery-bold-duotone",
    "settings-bold",
    "window-frame-bold-duotone",
];
const COL_PARTS = ["col-12", "col-md-6", "col-lg-4"];

export default function BsResponsive() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const [bp, setBp] = useState("desktop");
    const conf = BREAKPOINTS[bp];

    return (
        <section id="bs-responsive" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Bs_grid_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Bs_grid_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:bootstrap"
                            className="w-4 h-4"
                            aria-hidden="true"
                        />
                        grid
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Bs_grid_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {/* Choix de l'écran */}
                    <div className="flex flex-wrap items-center justify-center gap-2.5 mb-4">
                        {ORDER.map((key) => {
                            const b = BREAKPOINTS[key];
                            const active = bp === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setBp(key)}
                                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold ring-1 transition active:scale-95 ${
                                        active
                                            ? "bg-purple-500/15 ring-purple-400/50 text-purple-200"
                                            : "bg-white/[0.04] ring-white/10 text-gray-400 hover:bg-white/[0.07]"
                                    }`}
                                >
                                    <SolarIcon name={b.icon} className="w-4 h-4" />
                                    {t(b.labelKey)}
                                </button>
                            );
                        })}
                    </div>

                    {/* Classe de grille */}
                    <div className="flex justify-center mb-6">
                        <code className="font-mono text-[12.5px]">
                            <span className="text-gray-500">{"<div class=\""}</span>
                            {COL_PARTS.map((part) => (
                                <span
                                    key={part}
                                    className={`transition-colors duration-300 ${
                                        part === conf.col
                                            ? "text-purple-300 font-semibold"
                                            : "text-gray-500"
                                    }`}
                                >
                                    {part}{" "}
                                </span>
                            ))}
                            <span className="text-gray-500">{"\">"}</span>
                        </code>
                    </div>

                    {/* Aperçu responsive */}
                    <div className="rounded-2xl bg-black/20 ring-1 ring-white/10 p-4 sm:p-6 flex justify-center">
                        <motion.div
                            animate={{ maxWidth: conf.maxW }}
                            transition={{ duration: reduce ? 0 : 0.5, ease: "easeInOut" }}
                            className="w-full"
                            style={{ maxWidth: conf.maxW }}
                        >
                            {/* Mini-navigateur */}
                            <div className="rounded-xl bg-white ring-1 ring-slate-200 shadow-xl overflow-hidden">
                                <div className="h-7 bg-slate-100 border-b border-slate-200 flex items-center px-3 gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-slate-300" />
                                    <span className="w-2 h-2 rounded-full bg-slate-300" />
                                    <span className="w-2 h-2 rounded-full bg-slate-300" />
                                </div>
                                <div className={`p-3 grid ${conf.cols} gap-3`}>
                                    {CARD_ICONS.map((icon, i) => (
                                        <motion.div
                                            key={i}
                                            layout
                                            transition={{
                                                duration: reduce ? 0 : 0.4,
                                                ease: "easeInOut",
                                            }}
                                            className="rounded-lg bg-white ring-1 ring-slate-200 shadow-sm p-3"
                                        >
                                            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-100 text-purple-600 mb-2">
                                                <SolarIcon
                                                    name={icon}
                                                    className="w-4 h-4"
                                                />
                                            </span>
                                            <div className="h-2 rounded-full bg-slate-200 w-3/4" />
                                            <div className="mt-1.5 h-2 rounded-full bg-slate-100 w-1/2" />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <p className="mt-5 text-center text-sm text-gray-300 mb-0">
                        {t("Bs_grid_explain")}
                    </p>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Bs_grid_caption")}
            </p>
        </section>
    );
}
